import { useContext } from "react"
import { TLOContext } from "web/contexts"
import { capitalize } from "web/services/inflector"
import { NavigationProvider } from "web/contexts/Navigation"

import MobileExpander from "../../Mobile/Expander"

import ConditionPill from "./ConditionPill"
import SpatialTypeDistanceConditionPill from "./SpatialTypeDistanceConditionPill"
import { AddConditionButton } from "./Buttons"

const RECOMMENDED_FILTERS = {
  asset: [
    "asset_sector",
    "asset_city",
    "asset_state",
    "asset_owned",
    "asset_country",
    "asset_uses",
    "asset_year_built",
    "asset_owned",
  ],
  deal: [
    "deal_kind",
    "deal_status",
    "deal_sector",
    "deal_portfolios",
    "deal_partner",
    "deal_capital_type",
    "deal_property_status",
    "deal_deal_lead",
    "deal_city",
    "deal_state",
    "deal_country",
    "deal_total_investment",
    "deal_members",
  ],
  task: [
    "task_completed_by",
    "task_completed_on",
    "task_due_on",
    "task_assigned_users",
    "task_category",
  ],
  portfolio: ["portfolio_partner", "portfolio_sector"],
  sale: [
    "deal_name",
    "deal_address",
    "deal_city",
    "deal_state",
    "deal_country",
    "deal_seller",
    "deal_buyer",
    "deal_broker_agent",
    "deal_closed_date",
    "deal_purchase_price",
  ],
}

const COMPARABLE_FILTERS = {
  sale: [
    "deal_name",
    "deal_sector",
    "deal_seller",
    "deal_buyer",
    "deal_implied_cap_rate",
    "deal_city",
    "deal_deal_date",
    "deal_total_rentable_area",
    "deal_average_year_built",
  ],
  lease: [
    "asset_metro",
    "asset_submarket",
    "asset_city",
    "asset_year_built",
    "start_date",
    "area_leased",
    "term",
  ],
  unit_type: [
    "asset_metro",
    "asset_submarket",
    "asset_city",
    "asset_year_built",
    "unit_type",
    "average_area",
  ],
}

const SALE_COMPARABLE_LIST_TYPES = [
  "deal_comparable_deals",
  "deal_find_comparable_deals",
]

const TOPIC_RECORD_COMPARABLE_LIST_TYPE_PREFIXES = [
  "deal_comparable_topic_records",
  "deal_find_comparable_topic_records",
]

const isSaleComparableListType = (listType) =>
  SALE_COMPARABLE_LIST_TYPES.includes(listType)

const isTopicRecordComparableListType = (listType) =>
  TOPIC_RECORD_COMPARABLE_LIST_TYPE_PREFIXES.some((prefix) =>
    listType.startsWith(prefix)
  )

export default function ViewFilters({
  subjectType,
  listType = "",
  topic = null,
  availableFields,
  filterConditions = [],
  groupConditions = [],
  stickyConditions = [],
  handleAddCondition,
  handleChangeCondition,
  handleDeleteCondition,
  handleAddFieldToConditions,
  handleAddDistanceToConditions,
  handleChangeDistanceCondition,
  containerClasses,
}) {
  const validFilterKeys = {
    deal: ["deal"],
    asset: ["deal_asset", "asset"],
    portfolio: ["portfolio"],
    task: ["task"],
    sale: ["sale"],
  }

  function getRecommendationKeyForFilters(subjectType) {
    const filterKeys = Object.keys(validFilterKeys)
    let result = ""

    filterKeys.forEach((key) => {
      if (validFilterKeys[key].indexOf(subjectType) !== -1) {
        result = key
      }
    })

    return result
  }

  const { data } = useContext(TLOContext) || {}
  const recommendationKey = getRecommendationKeyForFilters(subjectType)
  const stickyConditionFieldKeys = stickyConditions.map((sc) => sc.field)
  const stickyConditionsWithValues = stickyConditions.map(
    (stickyCondition) =>
      filterConditions.find((fc) => fc.field === stickyCondition.field) ||
      stickyCondition
  )

  const nonStickyFilters = filterConditions.filter(
    (fc) => !stickyConditionFieldKeys.includes(fc.field)
  )

  const fieldKeysInUse = filterConditions
    .concat(groupConditions)
    .concat(stickyConditions)
    .map((el) => el.field)

  const potentialNewFilterFields = availableFields.filter(
    (f) => !fieldKeysInUse.includes(f.key)
  )

  function handleAddDistanceCondition(object) {
    if (!data) return
    handleAddDistanceToConditions({
      ...object,
      subject_type: capitalize(data.resource_type),
      subject_id: data.id,
    })
  }

  const isTopicRecordComparable = isTopicRecordComparableListType(listType)
  const hasComparableFilters =
    isSaleComparableListType(listType) || isTopicRecordComparable
  const comparableKey = isTopicRecordComparable ? topic?.name : "sale"
  const recommendedFilters = hasComparableFilters
    ? COMPARABLE_FILTERS[comparableKey]
    : RECOMMENDED_FILTERS[recommendationKey]

  const hasDistanceFilterSelected = filterConditions.some(
    (condition) => condition.type === "SpatialTypeDistanceCondition"
  )

  return (
    <NavigationProvider subjectType={subjectType}>
      <div className={containerClasses}>
        <MobileExpander>
          <div className="-ml-2 -mt-2 flex flex-wrap items-center pb-2">
            {[...stickyConditionsWithValues, ...nonStickyFilters].map(
              (condition) => {
                if (condition.type === "SpatialTypeDistanceCondition") {
                  return (
                    <SpatialTypeDistanceConditionPill
                      key={condition.id}
                      condition={condition}
                      onAdd={handleAddCondition}
                      onChange={handleChangeDistanceCondition}
                      onDelete={handleDeleteCondition}
                    />
                  )
                }
                const field = availableFields.find(
                  (el) => el.key === condition.field
                )
                if (!field) {
                  return null
                }

                return (
                  <ConditionPill
                    key={condition.id}
                    field={field}
                    condition={condition}
                    onAdd={handleAddCondition}
                    onChange={handleChangeCondition}
                    onDelete={handleDeleteCondition}
                  />
                )
              }
            )}
            <div
              className={
                "mt-2 flex flex-grow justify-center py-2 md:justify-end md:py-0"
              }
            >
              <AddConditionButton
                recommendedFilters={recommendedFilters}
                hasComparableFilters={hasComparableFilters}
                fields={potentialNewFilterFields}
                handleAddFieldCondition={handleAddFieldToConditions}
                handleAddDistanceCondition={handleAddDistanceCondition}
                hasDistanceFilterSelected={hasDistanceFilterSelected}
              />
            </div>
          </div>
        </MobileExpander>
      </div>
    </NavigationProvider>
  )
}
